import React, { useCallback, useRef, useState } from 'react';
import type { MapRef } from 'react-map-gl';
import type { PopupData } from '../mapModes/MapModeStrategy';
import type { ResumeDigitalTwin } from '@/domain/DigitalTwin/DigitalTwin';

export type MmsAttributeValue = string | number;

export interface MmsAssetAttribute {
	name: string;
	value: string | number | null;
	numeric?: boolean;
	editable?: boolean;
	unit?: string | null;
	ranges?: MmsAttributeValue[];
}

export interface MmsPopupAdditionalData {
	mmsId?: string;
	onClose: () => void;
	setAttributesValue: (attributes: MmsAssetAttribute[]) => void;
	onUpdateAttributeValue: (attributeName: string, newValue: MmsAttributeValue) => void;
	onDelete: () => void;
	onRestore: () => void;
}

export interface StrategyPopupAdditionalData {
	selectedDigitalTwin?: ResumeDigitalTwin;
	isAdmin: boolean;
}

export type PopupAdditionalData = MmsPopupAdditionalData | StrategyPopupAdditionalData;

export interface OpenPopupParams {
	data: PopupData;
	coordinates: [number, number];
	additionalData?: PopupAdditionalData;
}

export function useMapPopup(mapRef: React.RefObject<MapRef>) {
	const [showPopup, setShowPopup] = useState<boolean>(false);
	const [infraInfo, setInfraInfo] = useState<PopupData | null>(null);
	const [popupCoordinates, setPopupCoordinates] = useState<[number, number]>([0, 0]);
	const [popupAdditionalData, setPopupAdditionalData] = useState<PopupAdditionalData | null>(null);
	const [isPopupDragging, setIsPopupDragging] = useState(false);
	const popupCoordinatesRef = useRef<[number, number]>(popupCoordinates);
	popupCoordinatesRef.current = popupCoordinates;
	const dragStartRef = useRef<{ clientX: number; clientY: number; projX: number; projY: number } | null>(null);

	const openPopup = useCallback(({ data, coordinates, additionalData }: OpenPopupParams) => {
		setInfraInfo(data);
		setPopupCoordinates(coordinates);
		setPopupAdditionalData(additionalData ?? null);
		setShowPopup(true);
	}, []);

	const closePopup = useCallback(() => {
		setShowPopup(false);
		setInfraInfo(null);
	}, []);

	const hidePopup = useCallback(() => {
		setShowPopup(false);
	}, []);

	const onDrag = useCallback((e: MouseEvent) => {
		if (!mapRef.current || !dragStartRef.current) return;
		const map = mapRef.current.getMap();
		const { clientX: startX, clientY: startY, projX, projY } = dragStartRef.current;
		const dx = e.clientX - startX;
		const dy = e.clientY - startY;
		const coordinates = map.unproject([projX + dx, projY + dy]);
		setPopupCoordinates([coordinates.lng, coordinates.lat]);
	}, []);

	const onDragEnd = useCallback(() => {
		dragStartRef.current = null;
		setIsPopupDragging(false);
		document.removeEventListener('mousemove', onDrag);
		document.removeEventListener('mouseup', onDragEnd);
	}, [onDrag]);

	const handlePopupDragMouseDown = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		if (!mapRef.current) return;
		const map = mapRef.current.getMap();
		const projected = map.project(popupCoordinatesRef.current);
		dragStartRef.current = {
			clientX: e.clientX,
			clientY: e.clientY,
			projX: projected.x,
			projY: projected.y,
		};
		setIsPopupDragging(true);
		document.addEventListener('mousemove', onDrag);
		document.addEventListener('mouseup', onDragEnd);
	}, [onDrag, onDragEnd]);

	return {
		showPopup,
		infraInfo,
		popupCoordinates,
		popupAdditionalData,
		isPopupDragging,
		openPopup,
		closePopup,
		hidePopup,
		setShowPopup,
		setInfraInfo,
		setPopupCoordinates,
		setPopupAdditionalData,
		handlePopupDragMouseDown,
	};
}
